import React from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Container } from "react-bootstrap";
import { caseStudies, meta } from "../../content_option";
import "./style.css";

export const CaseStudyWrapper = () => {
  const { projectName } = useParams();
  const caseStudy = caseStudies[projectName];

  // If case study not found, show 404-like message
  if (!caseStudy) {
    return (
      <HelmetProvider>
        <Container className="case-study-container">
          <Helmet>
            <title>Case Study Not Found | {meta.title}</title>
          </Helmet>
          <div className="case-study-not-found">
            <h1>Case Study Not Found</h1>
            <p>The case study you're looking for doesn't exist.</p>
            <Link to="/portfolio" className="back-link">
              ← Back to Portfolio
            </Link>
          </div>
        </Container>
      </HelmetProvider>
    );
  }

  const {
    meta: csMeta,
    client,
    role,
    timeline,
    challenge,
    goals,
    process,
    solutions,
    metrics,
    testimonials,
    gallery,
    pdfDownloadUrl,
  } = caseStudy;

  return (
    <HelmetProvider>
      <Container className="case-study-container">
        <Helmet>
          <title>{csMeta.title} | {meta.title}</title>
          <meta name="description" content={csMeta.description} />
        </Helmet>

        {/* Back Navigation */}
        <div className="case-study-nav">
          <Link to="/portfolio" className="back-link">
            ← Back to Portfolio
          </Link>
        </div>

        {/* Hero Section */}
        <header
          className="case-study-hero"
          style={{ backgroundImage: `url(${csMeta.coverImage})` }}
        >
          <div className="hero-overlay">
            <span className="kicker">
              {client?.name} — {client?.industry}
            </span>
            <h1 className="case-study-title">{csMeta.title}</h1>
            <p className="case-study-lead">{csMeta.description}</p>
            <div className="meta-row">
              <span className="role-badge">{role}</span>
              <span className="dot">•</span>
              <time dateTime={csMeta.publishedDate}>{timeline}</time>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="case-study-main">
          {/* Overview Section */}
          <section className="cs-section">
            <h2>Overview</h2>
            <p className="overview-text">{challenge}</p>
            {goals && goals.length > 0 && (
              <>
                <h3>Goals</h3>
                <ul className="goal-list">
                  {goals.map((g, i) => (
                    <li key={i}>{g}</li>
                  ))}
                </ul>
              </>
            )}
          </section>

          {/* Process Section */}
          {process && process.length > 0 && (
            <section className="cs-section">
              <h2>Process</h2>
              <ol className="process-timeline">
                {process.map((step, i) => (
                  <li key={i} className="process-step">
                    <strong>{step.step}</strong>
                    <p>{step.body}</p>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* Solutions Section */}
          {solutions && solutions.length > 0 && (
            <section className="cs-section">
              <h2>Solutions</h2>
              <div className="solutions-grid">
                {solutions.map((s, i) => (
                  <div key={i} className="solution-card">
                    <p>{s}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Metrics Section */}
          {metrics && metrics.length > 0 && (
            <section className="cs-section">
              <h2>Impact & Metrics</h2>
              <div className="metrics-grid">
                {metrics.map((m, i) => (
                  <div key={i} className="metric-card">
                    <div className="metric-value">{m.value}</div>
                    <div className="metric-label">{m.label}</div>
                    {m.note && <div className="metric-note">{m.note}</div>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Gallery Section */}
          {gallery && gallery.length > 0 && (
            <section className="cs-section">
              <h2>Gallery</h2>
              <div className="gallery-grid">
                {gallery.map((img, i) => (
                  <figure key={i} className="gallery-item">
                    <img
                      src={img.src}
                      srcSet={img.srcSet}
                      alt={img.alt}
                      loading="lazy"
                    />
                    {img.caption && <figcaption>{img.caption}</figcaption>}
                  </figure>
                ))}
              </div>
            </section>
          )}

          {/* Testimonials Section */}
          {testimonials && testimonials.length > 0 && (
            <section className="cs-section">
              <h2>Testimonials</h2>
              {testimonials.map((t, i) => (
                <blockquote key={i} className="testimonial-card">
                  <p>"{t.quote}"</p>
                  <footer>
                    — {t.author}, {t.role}
                  </footer>
                </blockquote>
              ))}
            </section>
          )}

          {/* CTA Section */}
          <aside className="cta-section">
            {pdfDownloadUrl && (
              <a
                className="cta-download"
                href={pdfDownloadUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download Full Case Study (PDF)
              </a>
            )}
            {client?.url && (
              <a
                className="cta-live"
                href={client.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Live Project
              </a>
            )}
            <Link className="cta-contact" to="/contact">
              Discuss a Similar Project
            </Link>
          </aside>
        </main>
      </Container>
    </HelmetProvider>
  );
};

export default CaseStudyWrapper;
